// THE OPEN ENTRY (UI rebuild batch 2) — `?entry=<txn>` read by its id (GET /api/movements/:txn), so a link opens the
// receipt even when the row is not on the loaded page. The row the list already holds is drawn at once; the read's answer
// replaces it when it lands (and adds the postings under More).
//   404, no loaded row  → "This entry isn't in the books" — never a crash, never an empty receipt
//   404 over a loaded row → the loaded row, no postings: a BFF that predates the route answers 404 for every entry, and
//                         the row the list read IS in the books (no claim drawn from a route that is not there yet)
//   failed, loaded row  → the loaded row, one quiet line; failed, nothing loaded → the failure and Try again
//   movement: null      → an entry the list does not show: its postings alone, no Reverse door
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import { ApiError } from '../../../lib/errors';
import { ReadFailed } from '../../OnboardingParts';
import type { MovementLineRow } from '../../MovementTable';
import type { ReversedOutcome } from '../../ReverseEntry';
import type { TradeRow } from '../../../lib/api';
import { EntryMissing, EntryPostings, EntryShell, EntrySheet, EntryWaiting } from './EntrySheet';

export function EntryView({ txn, loaded, entity, dealsById, onOpenDeal, onReversed, onClose }: {
  txn: string;
  loaded: MovementLineRow | null;
  entity: string;
  dealsById: Map<string, TradeRow>;
  onOpenDeal: (dealId: string) => void;
  onReversed: (o: ReversedOutcome) => void;
  onClose: () => void;
}) {
  // Under ['movements'] so every money view's refresh (refreshMoneyViews) reads it again.
  const q = useQuery({
    queryKey: ['movements', 'entry', entity, txn],
    queryFn: () => api.movement(txn, entity),
    retry: (n, e) => !(e instanceof ApiError && (e.status === 404 || e.status === 400)) && n < 2,
  });
  const status = q.error instanceof ApiError ? q.error.status : null;
  const notFound = q.isError && (status === 404 || status === 400);
  const m = q.data?.movement ?? loaded;
  const postings = q.data ? <EntryPostings postings={q.data.postings} />
    : q.isPending ? <p className="text-meta text-tg-hint">Reading…</p> : null;

  if (m) {
    return (
      <EntrySheet
        m={m}
        entity={entity}
        dealsById={dealsById}
        onOpenDeal={onOpenDeal}
        onReversed={onReversed}
        onClose={onClose}
        postings={postings}
        notice={q.isError && !notFound ? <ReadFailed what="this entry" error={q.error} says="showing the listed row" retry={() => { void q.refetch(); }} /> : undefined}
      />
    );
  }
  if (q.data) {
    // movement: null — an entry the Money screen does not list (a memo pair, a month's close): its postings, nothing more.
    return (
      <EntryShell onClose={onClose}>
        <div className="mb-2 text-label font-medium uppercase tracking-wider text-tg-hint">Postings</div>
        <EntryPostings postings={q.data.postings} />
      </EntryShell>
    );
  }
  if (notFound) return <EntryMissing onClose={onClose} />;
  if (q.isError) {
    return <EntryShell onClose={onClose}><ReadFailed what="this entry" error={q.error} retry={() => { void q.refetch(); }} /></EntryShell>;
  }
  return <EntryWaiting onClose={onClose} />;
}
